import * as React from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import {
    Box,
    Button,
    Container,
    FormControlLabel,
    Grid,
    ListSubheader,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    TextareaAutosize,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { Fragment, useEffect } from 'react';
import frLocale from 'date-fns/locale/fr';
import MainCard from '../ui-component/cards/MainCard';
import { Diagnosis } from '../ui-component/diagnosis';
import ICD10Generator from '../utils/icd10generator';

export type hearingAid = {
    id: string;
    StockCode: string;
    NappiCode: string;
    Description: string;
    Type: string;
    ChargeType: string;
    Price: string;
    Brand: string;
};

const Generator = () => {
    const [hearingAids, setHearingAids] = React.useState<[hearingAid]>([
        {
            id: '',
            StockCode: '',
            Price: '',
            Type: '',
            NappiCode: '',
            ChargeType: '',
            Description: '',
            Brand: ''
        }
    ]);

    const [uniqueType, setUniqueType] = React.useState<hearingAid[]>([
        {
            id: '',
            StockCode: '',
            Price: '',
            Type: '',
            NappiCode: '',
            ChargeType: '',
            Description: '',
            Brand: ''
        }
    ]);
    const [uniqueNappi, setUniqueNappi] = React.useState<hearingAid[]>([
        {
            id: '',
            StockCode: '',
            Price: '',
            Type: '',
            NappiCode: '',
            ChargeType: '',
            Description: '',
            Brand: ''
        }
    ]);

    const [value, setValue] = React.useState(null);
    const [dob, setDob] = React.useState(null);
    const titles = ['Mr', 'Mrs', 'Miss', 'Ms'];
    const shapes = ['Flat', 'Sloping', 'Reverse sloping', 'U-shaped', 'Reverse U-shaped', 'High frequency', 'Not defined'];
    const [name, setName] = React.useState('');
    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);
    };

    const [lastName, setLastName] = React.useState('');
    const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(event.target.value);
    };

    const [medical, setMedical] = React.useState('');
    const handleMedicalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMedical(event.target.value);
    };

    const [medicalNumber, setMedicalNumber] = React.useState('');
    const handleMedicalNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMedicalNumber(event.target.value);
    };

    const [diagnosis, setDiagnosis] = React.useState('bilateral mild to moderate sloping sensory-neural');
    const handleDiagnosisChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDiagnosis(event.target.value);
    };

    const [hearing, setHearing] = React.useState('');
    const [comment, setComment] = React.useState('');

    const handleChange = (event: SelectChangeEvent<typeof hearing>) => {
        setHearing(event.target.value);
    };

    const [title, setTitle] = React.useState('Mr');
    const [reference, setReference] = React.useState('');

    const [aidClaim, setAidClaim] = React.useState('both');

    const handleTitleChange = (event: SelectChangeEvent<typeof title>) => {
        setTitle(event.target.value);
    };

    useEffect(() => {
        const fetchData = async () =>
            await axios.get('/api/list').then((value) => {
                setHearingAids(value.data);
            });
        fetchData();
    }, []);

    useEffect(() => {
        //alert(JSON.stringify(hearingAids))
        const unappi = hearingAids.filter((v, i, a) => a.findIndex((t) => t.NappiCode === v.NappiCode) === i);
        setUniqueNappi(unappi);
        const uniType = hearingAids.filter((v, i, a) => a.findIndex((t) => t.Type === v.Type) === i);
        setUniqueType(uniType);
    }, [hearingAids]);

    const [leftType, setLeftType] = React.useState(0);
    const [rightType, setRightType] = React.useState(0);
    const [rightShape, setRightShape] = React.useState('Not defined');
    const [leftShape, setLeftShape] = React.useState('Not defined');

    const [leftIntensity, setLeftIntensity] = React.useState(0);
    const [rightIntensity, setRightIntensity] = React.useState(0);
    const [leftToIntensity, setToLeftIntensity] = React.useState(-1);
    const [rightToIntensity, setToRightIntensity] = React.useState(-1);

    function quote() {
        return axios
            .post(
                '/api/download/quote',
                {
                    recommended: hearing,
                    name: name,
                    DOB: dob,
                    membershipNo: medicalNumber,
                    medicalAid: medical,
                    lastName: lastName,
                    title: title,
                    ICD10Code: ICD10Generator(rightType, leftType),
                    testDate: value,
                    leftIntensity: leftIntensity,
                    rightIntensity: rightIntensity,
                    leftToIntensity: leftToIntensity,
                    rightToIntensity: rightToIntensity,
                    reference: reference,
                    leftShape: leftShape,
                    rightShape: rightShape
                },
                { responseType: 'blob' }
            )
            .then(function (response) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'file.docx'); //or any other extension
                document.body.appendChild(link);
                link.click();
            })
            .catch(function (error) {
                console.log(error);
            });
    }

    function motivation() {
        return axios
            .post(
                '/api/download/motivation',
                {
                    recommended: hearing,
                    name: name,
                    DOB: dob,
                    membershipNo: medicalNumber,
                    medicalAid: medical,
                    lastName: lastName,
                    title: title,
                    ICD10Code: ICD10Generator(rightType, leftType),
                    testDate: value,
                    leftIntensity: leftIntensity,
                    rightIntensity: rightIntensity,
                    leftToIntensity: leftToIntensity,
                    rightToIntensity: rightToIntensity,
                    leftShape: leftShape,
                    rightShape: rightShape,
                    additional: comment
                },
                { responseType: 'blob' }
            )
            .then(function (response) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'file.docx'); //or any other extension
                document.body.appendChild(link);
                link.click();
            })
            .catch(function (error) {
                console.log(error);
            });
    }

    return (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <MainCard title="Generator">
            <Typography variant="h4" component="div" gutterBottom>
                Patient Details
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Select onChange={handleTitleChange} value={title} variant={'standard'} sx={{ mt: 2 }}>
                        {titles.map((value1, index, array) => {
                            return (
                                <MenuItem key={index} value={value1}>
                                    {value1}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    <TextField
                        id="outlined-name"
                        label="Name"
                        value={name}
                        onChange={handleNameChange}
                        variant={'standard'}
                        sx={{ ml: 2 }}
                    />
                    <TextField
                        id="outlined-name"
                        label="Surname"
                        value={lastName}
                        onChange={handleLastNameChange}
                        variant={'standard'}
                        sx={{ ml: 2 }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField id="outlined-name" label="Medical Aid" value={medical} onChange={handleMedicalChange} variant={'standard'} />
                    <TextField
                        id="outlined-name"
                        label="Membership Number"
                        value={medicalNumber}
                        onChange={handleMedicalNumberChange}
                        variant={'standard'}
                        sx={{ ml: 2 }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} value={frLocale}>
                        <DatePicker
                            views={['year', 'month', 'day']}
                            label="Date of birth"
                            value={dob}
                            onChange={(newValue) => {
                                setDob(newValue);
                            }}
                            renderInput={(params) => <TextField {...params} />}
                            inputFormat="dd-MM-yyyy"
                        />
                    </LocalizationProvider>
                </Grid>
            </Grid>

            <Typography variant="h4" component="div" gutterBottom sx={{ mt: 2, mb: 2 }}>
                Diagnosis
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDateFns} value={frLocale}>
                <DatePicker
                    views={['year', 'month', 'day']}
                    label="Test Date"
                    value={value}
                    onChange={(newValue) => {
                        setValue(newValue);
                    }}
                    renderInput={(params) => <TextField {...params} />}
                    inputFormat="dd-MM-yyyy"
                />
            </LocalizationProvider>

            <Stack direction="row" gap={10} sx={{ mt: '20px' }}>
                <div>
                    <Typography>Right Ear</Typography>
                    <Diagnosis
                        onTypeChange={(arg0) => setRightType(arg0)}
                        onIntensityChange={(arg0) => setRightIntensity(arg0)}
                        onIntensityToChange={(arg0) => setToRightIntensity(arg0)}
                    />

                    <Typography>Shape</Typography>
                    <Select
                        onChange={(event: SelectChangeEvent<typeof rightShape>) => {
                            setRightShape(event.target.value);
                        }}
                        value={rightShape}
                        sx={{ mt: 2 }}
                    >
                        {shapes.map((value1, index, array) => {
                            return (
                                <MenuItem key={index} value={value1}>
                                    {value1}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </div>
                <div>
                    <Typography>Left Ear</Typography>
                    <Diagnosis
                        onTypeChange={(arg0) => setLeftType(arg0)}
                        onIntensityChange={(arg0) => setLeftIntensity(arg0)}
                        onIntensityToChange={(arg0) => setToLeftIntensity(arg0)}
                    />

                    <Typography>Shape</Typography>
                    <Select
                        onChange={(event: SelectChangeEvent<typeof leftShape>) => {
                            setLeftShape(event.target.value);
                        }}
                        value={leftShape}
                        sx={{ mt: 2 }}
                    >
                        {shapes.map((value1, index, array) => {
                            return (
                                <MenuItem key={index} value={value1}>
                                    {value1}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </div>
            </Stack>

            {/*<TextField id="outlined-name" label="Diagnosis" value={diagnosis} onChange={handleDiagnosisChange} />*/}

            <Typography variant="h4" component="div" gutterBottom sx={{ mt: 2, mb: 2 }}>
                Hearing Aid
            </Typography>

            <ToggleButtonGroup
                color="primary"
                value={aidClaim}
                exclusive
                onChange={(event: React.MouseEvent<HTMLElement>, newAlignment: string) => setAidClaim(newAlignment)}
            >
                <ToggleButton value="left">Left Only</ToggleButton>
                <ToggleButton value="both">Both</ToggleButton>
                <ToggleButton value="right">Right Only</ToggleButton>
            </ToggleButtonGroup>

            <br></br>
            <br></br>

            <Select defaultValue="" id="grouped-select" label="Grouping" value={hearing} onChange={handleChange}>
                {uniqueType.map((object, i) => {
                    const r = <ListSubheader key={i}>{object.Type}</ListSubheader>;
                    let renderMe = uniqueNappi.filter((aid) => {
                        return aid.Type == object.Type;
                    });

                    renderMe = renderMe.sort((a1, b) => {
                        return Number.parseFloat(a1.Price) - Number.parseFloat(b.Price);
                    });
                    const a = renderMe.map((object, i) => (
                        <MenuItem key={object.NappiCode} value={object.NappiCode}>
                            {object.Description} ({object.ChargeType}) - R{object.Price}
                        </MenuItem>
                    ));
                    return [r, a];
                })}
            </Select>

            <Typography variant="h4" component="div" gutterBottom sx={{ mt: 2, mb: 2 }}>
                Other
            </Typography>
            <TextField
                id="outlined-name"
                label="Quote Reference"
                value={reference}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setReference(event.target.value);
                }}
                variant={'standard'}
            />

            <Typography component="div" gutterBottom sx={{ mt: 2, mb: 2 }}>
                Additional Comments
            </Typography>

            <TextareaAutosize
                id="outlined-name"
                placeholder="Additional Comments"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
            />

            <Stack direction="row" gap={5} sx={{ mt: '20px' }}>
                <Button onClick={quote} variant="contained">
                    Generate Quote
                </Button>
                <Button onClick={motivation} variant="contained">
                    Generate Motivation
                </Button>
            </Stack>
        </MainCard>
    );
};

export default Generator;
